import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { useLinkHandler } from '../lib/router.js';
import { ButtonLink } from '../design/index.js';
import type { ButtonSize, ButtonTone, ButtonVariant } from '../design/index.js';

/**
 * An internal link.
 *
 * A real `<a href>` that the router intercepts, never a button that navigates.
 * The difference shows up the first time somebody middle-clicks a call to open
 * it in a second tab, or copies the link address to send to a colleague — and
 * it is the difference between a web page and an application pretending to be
 * one.
 *
 * A caller's own `onClick` runs **as well as** the router's, not instead of
 * it. Spreading `rest` over the handler is how the phone's navigation drawer
 * — whose links close the menu on click — quietly became a set of full page
 * loads: the session lives in memory and nothing else, so every tap on a
 * phone signed the salon out and returned them to the token screen. The order
 * here is deliberate, and so is the early return in `useLinkHandler`: a caller
 * that calls `preventDefault` still cancels the navigation.
 */
export function Link({
  href,
  children,
  onClick,
  ...rest
}: { readonly href: string; readonly children: ReactNode } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'children'
>): React.JSX.Element {
  const handle = useLinkHandler();
  return (
    <a
      {...rest}
      href={href}
      onClick={(event) => {
        onClick?.(event);
        handle(href, event);
      }}
    >
      {children}
    </a>
  );
}

/**
 * A link that looks like a button and stays inside the application.
 *
 * `ButtonLink` from the design system is a plain anchor, which is right for
 * leaving — the sign-in redirect, a `mailto:`. For an internal destination a
 * full page load throws away the session in memory and repaints the whole
 * shell, so these two are not interchangeable and the names say which is
 * which.
 */
export function NavButton({
  href,
  children,
  variant = 'primary',
  size = 'md',
  tone = 'light',
  block = false,
}: {
  readonly href: string;
  readonly children: ReactNode;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly tone?: ButtonTone;
  readonly block?: boolean;
}): React.JSX.Element {
  const handle = useLinkHandler();
  return (
    <ButtonLink
      href={href}
      variant={variant}
      size={size}
      tone={tone}
      block={block}
      onClick={(event) => {
        handle(href, event);
      }}
    >
      {children}
    </ButtonLink>
  );
}
